import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { ChevronLeft } from "lucide-react";
import ContainerPage from "../../components/Containers/ContainerPage";
import TitlePage from "../../components/Texts/TitlePage";
import Button from "../../components/Buttons/Button";
import Select from "../../components/Form/Select";
import Loader from "../../components/Loaders/Loader";
import ProduceRowsTable from "../../features/creativeTemplates/produce/ProduceRowsTable";
import ProduceResultsGallery from "../../features/creativeTemplates/produce/ProduceResultsGallery";
import { ConfirmAlert, SimpleAlert } from "../../components/Alerts/Alerts";
import { useDispatchApp } from "../../lib/redux";
import { useTranslationApp } from "../../lib/i18next";
import i18next from "i18next";
import { getPathDatabaseByRole } from "../../actions/auth";
import { getKeyDatabase } from "../../services/public";
import {
  startListenerCreativeTemplate,
  startListenerCreativeTemplateCards,
  startProduceCreativeTemplate,
  stopListenerCreativeTemplate,
  stopListenerCreativeTemplateCards,
} from "../../actions/creativeTemplates";
import {
  startListenerCreativeTemplateProduce,
  stopListenerCreativeTemplateProduce,
} from "../../actions/creativeTemplatesProduce";
import {
  CREATIVE_TEMPLATE_PRODUCE_STATUS,
  FONT_SIZE_BUTTON,
  FONT_WEIGHT_BUTTON,
} from "../../utils/constants";
import {
  ENGLISH,
  LOADING_INFORMATION,
  PORTUGUESE,
  SPANISH,
  TEMPLATES_PRODUCE_CHANGE,
  TEMPLATES_PRODUCE_CHANGE_TEMPLATE_DESCRIPTION,
  TEMPLATES_PRODUCE_CHANGE_TEMPLATE_TITLE,
  TEMPLATES_PRODUCE_CSV_APPEND,
  TEMPLATES_PRODUCE_CSV_DESCRIPTION,
  TEMPLATES_PRODUCE_CSV_EMPTY,
  TEMPLATES_PRODUCE_CSV_ERROR,
  TEMPLATES_PRODUCE_CSV_REPLACE,
  TEMPLATES_PRODUCE_CSV_TITLE,
  TEMPLATES_PRODUCE_DESCRIPTION,
  TEMPLATES_PRODUCE_DIMENSIONS,
  TEMPLATES_PRODUCE_EMPTY_TEMPLATES,
  TEMPLATES_PRODUCE_EMPTY_VARIABLES,
  TEMPLATES_PRODUCE_GENERATE,
  TEMPLATES_PRODUCE_LANGUAGE,
  TEMPLATES_PRODUCE_SELECT_TEMPLATE,
  TEMPLATES_PRODUCE_SUMMARY,
  TEMPLATES_PRODUCE_TEMPLATE,
  TEMPLATES_PRODUCE_TITLE,
  TEMPLATES_PRODUCE_VALIDATION_ERROR,
  TEMPLATES_PRODUCE_VALIDATION_NO_FORMATS,
  TEMPLATES_PRODUCE_VALIDATION_NO_ROWS,
  TEMPLATES_PRODUCE_EDIT_NO_INSTANCE,
  TEMPLATES_BACK,
  BUTTON_GO_BACK,
} from "../../i18n/keysTranslations";
import {
  createEmptyRow,
  getDefaultProduceLanguage,
  getInvalidRowIndexes,
  hasNonEmptyRows,
  listProduceColumns,
  listProduceCreatives,
  normalizeStoredIDs,
  normalizeStoredRows,
  resolveSelectedArtboardIDs,
  scrollToProduceRow,
  toggleSelectedID,
} from "../../features/creativeTemplates/produce/produceRows";
import {
  downloadProduceFormatCsv,
  parseProduceCsvFile,
} from "../../features/creativeTemplates/produce/produceCsv";
import ProduceToggleChips from "../../features/creativeTemplates/produce/ProduceToggleChips";
import { canProduceCreativeTemplate } from "../../features/creativeTemplates/utils/layerTree";
import {
  getArtboardChipLabel,
  getArtboardLabel,
  listArtboardIDs,
} from "../../features/creativeTemplates/utils/artboards";
import ModalCreativeTemplateEdit from "../../components/Modal/ModalCreativeTemplateEdit";

const optionalRules = { required: false };

const selectSx = {
  borderRadius: "10px",
};

const changeButtonSx = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  height: 32,
  px: 1.25,
  borderRadius: "8px",
  border: "1px solid #E4E4E7",
  bgcolor: "#fff",
  color: "text.primary",
  fontSize: "13px",
  fontWeight: 500,
  fontFamily: "inherit",
  appearance: "none",
  cursor: "pointer",
  flexShrink: 0,
  userSelect: "none",
  "&:disabled": {
    cursor: "not-allowed",
    opacity: 0.5,
  },
};

const TemplateProduce = ({
  initialTemplateID = "",
  initialProduceID = "",
  onBackToList = () => {},
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { userID } = dispatch(getPathDatabaseByRole());
  const cardsListenerRef = useRef(null);
  const docListenerRef = useRef(null);
  const produceListenerRef = useRef(null);
  const hydratedProduceIDRef = useRef(null);
  const ensuredProduceIDRef = useRef(false);
  const rowRefs = useRef({});
  const [cards, setCards] = useState({});
  const [isLoadingCards, setIsLoadingCards] = useState(true);
  const [isLoadingDoc, setIsLoadingDoc] = useState(false);
  const [doc, setDoc] = useState(null);
  const [invalidRowIndexes, setInvalidRowIndexes] = useState([]);
  const [isStartingProduce, setIsStartingProduce] = useState(false);
  const [produceRecord, setProduceRecord] = useState(null);
  const [isPickingTemplate, setIsPickingTemplate] = useState(false);
  const [selectedArtboardIDs, setSelectedArtboardIDs] = useState([]);
  const [artboardHydrateNonce, setArtboardHydrateNonce] = useState(0);
  const [editItem, setEditItem] = useState(null);
  const [produceID, setProduceID] = useState(initialProduceID || "");
  const storedArtboardIDsRef = useRef([]);

  const { control, watch, setValue, getValues } = useForm({
    defaultValues: {
      templateID: initialTemplateID || "",
      language: getDefaultProduceLanguage({ language: i18next.language }),
      rows: [],
    },
  });
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "rows",
  });

  const templateID = watch("templateID");
  const language = watch("language");
  const rows = watch("rows");

  const produceableCards = useMemo(
    () =>
      Object.values(cards)
        .filter((card) =>
          canProduceCreativeTemplate({ nodeCount: card.nodeCount })
        )
        .sort(
          (left, right) =>
            (right.updateTime || right.updatedAt || 0) -
            (left.updateTime || left.updatedAt || 0)
        ),
    [cards]
  );

  const selectedCard = produceableCards.find((card) => card.id === templateID);
  const columns = useMemo(
    () =>
      listProduceColumns({
        variables: doc?.variables || {},
        nodes: doc?.nodes || {},
        artboards: doc?.artboards || {},
      }),
    [doc]
  );
  const pathStorage = `adsCampaigns/${userID}/`;
  const produceStatus = produceRecord?.status || "";
  const isProducing =
    isStartingProduce ||
    produceStatus === CREATIVE_TEMPLATE_PRODUCE_STATUS.PROCESSING;
  const canEditRows = Boolean(
    templateID && columns.length > 0 && !isLoadingDoc && !isProducing
  );
  const galleryItems = useMemo(
    () => listProduceCreatives({ creatives: produceRecord?.creatives }),
    [produceRecord]
  );
  const availableArtboardIDs = useMemo(() => {
    const fromDoc = listArtboardIDs({ artboards: doc?.artboards });
    if (fromDoc.length > 0) return fromDoc;
    if (selectedCard?.artboardIDs?.length) return selectedCard.artboardIDs;
    return [];
  }, [doc, selectedCard]);
  const availableArtboardIDsKey = availableArtboardIDs.join(",");
  const selectedArtboardCount = Math.max(selectedArtboardIDs.length, 0);
  const produceArtboardCount = Math.max(
    produceRecord?.artboardCount || selectedArtboardCount || availableArtboardIDs.length,
    1
  );
  const totalCount = fields.length * Math.max(selectedArtboardCount, 1);
  const pendingCount = Math.max(
    (produceRecord?.itemCount || fields.length) * produceArtboardCount -
      galleryItems.length,
    0
  );
  const showGallery =
    isProducing ||
    produceStatus === CREATIVE_TEMPLATE_PRODUCE_STATUS.SUCCESS ||
    produceStatus === CREATIVE_TEMPLATE_PRODUCE_STATUS.ERROR ||
    galleryItems.length > 0;

  const templateOptions = produceableCards.map((card) => ({
    value: card.id,
    label: card.name,
  }));
  const languageOptions = [
    { value: "es", label: t(SPANISH) },
    { value: "pt", label: t(PORTUGUESE) },
    { value: "en", label: t(ENGLISH) },
  ];
  const formatOptions = availableArtboardIDs.map((artboardID) => ({
    value: artboardID,
    label: doc?.artboards?.[artboardID]
      ? getArtboardLabel({
          artboard: doc.artboards[artboardID],
          artboardID,
        })
      : getArtboardChipLabel({
          artboardID,
          cardArtboard: selectedCard?.artboard,
        }),
  }));
  const showFormatToggles = availableArtboardIDs.length > 1;
  const showTemplateSelect =
    produceableCards.length > 0 && (!templateID || isPickingTemplate);
  const templateName = selectedCard?.name || doc?.name || "";

  useEffect(() => {
    if (produceID) {
      ensuredProduceIDRef.current = true;
      return;
    }
    if (ensuredProduceIDRef.current) return;
    ensuredProduceIDRef.current = true;
    setProduceID(dispatch(getKeyDatabase()));
  }, [dispatch, produceID]);

  useEffect(() => {
    dispatch(
      startListenerCreativeTemplateCards({
        onUpdatedDataCallback: (value) => {
          setCards(value || {});
          setIsLoadingCards(false);
        },
        onReferenceAvailable: (dbRef) => {
          cardsListenerRef.current = dbRef;
        },
      })
    );

    return () => {
      dispatch(
        stopListenerCreativeTemplateCards({
          listenerRef: cardsListenerRef.current,
        })
      );
    };
  }, [dispatch]);

  useEffect(() => {
    if (isLoadingCards) return;
    const currentID = getValues("templateID");
    if (currentID && !produceableCards.some((card) => card.id === currentID)) {
      setValue("templateID", "");
      replace([]);
      setDoc(null);
      setInvalidRowIndexes([]);
    }
  }, [getValues, isLoadingCards, produceableCards, produceID, replace, setValue]);

  useEffect(() => {
    if (!templateID) {
      setDoc(null);
      setIsLoadingDoc(false);
      return undefined;
    }

    setIsLoadingDoc(true);
    dispatch(
      startListenerCreativeTemplate({
        templateID,
        onUpdatedDataCallback: (value) => {
          setDoc(value?.doc || null);
          setIsLoadingDoc(false);
        },
        onReferenceAvailable: (dbRef) => {
          docListenerRef.current = dbRef;
        },
      })
    );

    return () => {
      dispatch(
        stopListenerCreativeTemplate({ listenerRef: docListenerRef.current })
      );
    };
  }, [dispatch, templateID]);

  useEffect(() => {
    hydratedProduceIDRef.current = null;
    setProduceRecord(null);
    if (!produceID) return undefined;

    dispatch(
      startListenerCreativeTemplateProduce({
        produceID,
        onUpdatedDataCallback: (value) => {
          setProduceRecord(value);
          if (!value || hydratedProduceIDRef.current === produceID) return;
          hydratedProduceIDRef.current = produceID;
          if (value.templateID) setValue("templateID", value.templateID);
          if (value.language) setValue("language", value.language);
          storedArtboardIDsRef.current = normalizeStoredIDs({
            value: value.artboardIDs,
          });
          setArtboardHydrateNonce((nonce) => nonce + 1);
          const storedRows = normalizeStoredRows({ rows: value.rows });
          if (storedRows.length > 0) replace(storedRows);
        },
        onReferenceAvailable: (dbRef) => {
          produceListenerRef.current = dbRef;
        },
      })
    );

    return () => {
      dispatch(
        stopListenerCreativeTemplateProduce({
          listenerRef: produceListenerRef.current,
        })
      );
    };
  }, [dispatch, produceID]);

  useEffect(() => {
    if (!availableArtboardIDsKey) {
      setSelectedArtboardIDs([]);
      return;
    }
    const availableIDs = availableArtboardIDsKey.split(",");
    setSelectedArtboardIDs((prev) =>
      resolveSelectedArtboardIDs({
        availableIDs,
        selectedIDs: prev,
        storedIDs: storedArtboardIDsRef.current,
      })
    );
    storedArtboardIDsRef.current = [];
  }, [templateID, availableArtboardIDsKey, artboardHydrateNonce]);

  useEffect(() => {
    if (invalidRowIndexes.length === 0) return;
    const nextInvalid = getInvalidRowIndexes({ rows, columns });
    const isSame =
      nextInvalid.length === invalidRowIndexes.length &&
      nextInvalid.every(
        (rowIndex, index) => rowIndex === invalidRowIndexes[index]
      );
    if (!isSame) setInvalidRowIndexes(nextInvalid);
  }, [columns, invalidRowIndexes, rows]);

  const resetRows = () => {
    replace([]);
    setInvalidRowIndexes([]);
    rowRefs.current = {};
  };

  const onValidateTemplateChange = async (nextTemplateID) => {
    if (nextTemplateID === templateID) return true;
    const currentRows = getValues("rows") || [];
    if (!hasNonEmptyRows({ rows: currentRows, columns })) {
      resetRows();
      return true;
    }
    const confirmed = await ConfirmAlert({
      title: t(TEMPLATES_PRODUCE_CHANGE_TEMPLATE_TITLE),
      text: t(TEMPLATES_PRODUCE_CHANGE_TEMPLATE_DESCRIPTION),
    });
    if (!confirmed.isConfirmed) return false;
    resetRows();
    return true;
  };

  const onTemplateChange = () => {
    storedArtboardIDsRef.current = [];
    setSelectedArtboardIDs([]);
    setIsPickingTemplate(false);
  };

  const onToggleArtboard = (artboardID) => {
    setSelectedArtboardIDs((prev) =>
      toggleSelectedID({
        availableIDs: availableArtboardIDs,
        selectedIDs: prev,
        id: artboardID,
      })
    );
  };

  const onAddRow = () => {
    if (!canEditRows) return;
    append(createEmptyRow({ columns }));
    setInvalidRowIndexes([]);
  };

  const onRemoveRow = ({ index }) => {
    remove(index);
    setInvalidRowIndexes([]);
  };

  const onChangeImage = ({ index, key, nextUrl }) => {
    setValue(`rows.${index}.${key}`, nextUrl, {
      shouldDirty: true,
      shouldTouch: true,
    });
    setInvalidRowIndexes([]);
  };

  const applyCsvRows = ({ parsedRows, mode }) => {
    if (mode === "replace") {
      replace(parsedRows);
    } else {
      replace([...(getValues("rows") || []), ...parsedRows]);
    }
    setInvalidRowIndexes([]);
  };

  const onUploadCsv = async ({ file }) => {
    if (!canEditRows) return;
    let parsedRows = [];
    try {
      parsedRows = await parseProduceCsvFile({ file, columns });
    } catch {
      SimpleAlert({
        title: t(TEMPLATES_PRODUCE_CSV_ERROR),
        icon: "error",
      });
      return;
    }
    if (parsedRows.length === 0) {
      SimpleAlert({
        title: t(TEMPLATES_PRODUCE_CSV_EMPTY),
        icon: "warning",
      });
      return;
    }
    const currentRows = getValues("rows") || [];
    if (currentRows.length === 0) {
      applyCsvRows({ parsedRows, mode: "replace" });
      return;
    }
    const result = await ConfirmAlert({
      title: t(TEMPLATES_PRODUCE_CSV_TITLE),
      text: t(TEMPLATES_PRODUCE_CSV_DESCRIPTION),
      confirmButtonText: t(TEMPLATES_PRODUCE_CSV_REPLACE),
      thirdButtonText: t(TEMPLATES_PRODUCE_CSV_APPEND),
      cancelButtonText: t(BUTTON_GO_BACK),
      showThirdButton: true,
    });
    if (result.isConfirmed) {
      applyCsvRows({ parsedRows, mode: "replace" });
      return;
    }
    if (result.isDenied) {
      applyCsvRows({ parsedRows, mode: "append" });
    }
  };

  const onDownloadFormat = () => {
    if (!canEditRows) return;
    downloadProduceFormatCsv({
      columns,
      templateName: selectedCard?.name || doc?.name,
    });
  };

  const onGenerate = async () => {
    if (!templateID) {
      SimpleAlert({
        title: t(TEMPLATES_PRODUCE_SELECT_TEMPLATE),
        icon: "warning",
      });
      return;
    }
    const currentRows = getValues("rows") || [];
    if (currentRows.length === 0) {
      SimpleAlert({
        title: t(TEMPLATES_PRODUCE_VALIDATION_NO_ROWS),
        icon: "warning",
      });
      return;
    }
    const artboardIDsToProduce =
      selectedArtboardIDs.length > 0
        ? selectedArtboardIDs
        : availableArtboardIDs;
    if (artboardIDsToProduce.length === 0) {
      SimpleAlert({
        title: t(TEMPLATES_PRODUCE_VALIDATION_NO_FORMATS),
        icon: "warning",
      });
      return;
    }
    const nextInvalid = getInvalidRowIndexes({
      rows: currentRows,
      columns,
    });
    setInvalidRowIndexes(nextInvalid);
    if (nextInvalid.length > 0) {
      scrollToProduceRow({ rowRefs, index: nextInvalid[0] });
      SimpleAlert({
        title: t(TEMPLATES_PRODUCE_VALIDATION_ERROR),
        icon: "error",
      });
      return;
    }
    const nextProduceID = produceID || dispatch(getKeyDatabase());
    if (!produceID) {
      setProduceID(nextProduceID);
    }
    hydratedProduceIDRef.current = nextProduceID;
    setIsStartingProduce(true);
    try {
      const result = await dispatch(
        startProduceCreativeTemplate({
          templateID,
          produceID: nextProduceID,
          language,
          rows: currentRows,
          columns,
          artboardIDs: artboardIDsToProduce,
        })
      );
      if (result?.reason === "tooManyRows") {
        SimpleAlert({
          title: t(TEMPLATES_PRODUCE_VALIDATION_ERROR),
          icon: "warning",
        });
        return;
      }
    } finally {
      setIsStartingProduce(false);
    }
  };

  const onEditItem = ({ item } = {}) => {
    if (!item?.url) return;
    if (!item.instance?.nodes) {
      SimpleAlert({
        title: t(TEMPLATES_PRODUCE_EDIT_NO_INSTANCE),
        icon: "warning",
      });
      return;
    }
    setEditItem(item);
  };

  return (
    <ContainerPage hasMarginBottom={false} sx={{ height: "100%", pt: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Box
          component="button"
          type="button"
          onClick={onBackToList}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            mb: 0.5,
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
            color: "inherit",
            fontFamily: "inherit",
          }}
        >
          <ChevronLeft size={16} />
          <Typography
            sx={{
              fontSize: FONT_SIZE_BUTTON,
              fontWeight: FONT_WEIGHT_BUTTON,
            }}
          >
            {t(TEMPLATES_BACK)}
          </Typography>
        </Box>
        <TitlePage>{t(TEMPLATES_PRODUCE_TITLE)}</TitlePage>
        <Typography color="text.secondary">
          {t(TEMPLATES_PRODUCE_DESCRIPTION)}
        </Typography>
      </Box>
      {isLoadingCards ? (
        <Loader hasMessage message={t(LOADING_INFORMATION)} />
      ) : (
        <Box
          sx={{
            display: "flex",
            gap: 3,
            width: "100%",
            alignItems: "flex-start",
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", md: 320 },
              flexShrink: 0,
              bgcolor: "#fff",
              border: "1px solid #E4E4E7",
              borderRadius: "14px",
              p: 2,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>
              {t(TEMPLATES_PRODUCE_TEMPLATE)}
            </Typography>
            <Box
              sx={{
                width: "100%",
                aspectRatio: "1",
                borderRadius: "10px",
                backgroundColor: "#EEE",
                backgroundImage: selectedCard?.thumbnailUrl
                  ? `url(${selectedCard.thumbnailUrl})`
                  : "none",
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
            {produceableCards.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t(TEMPLATES_PRODUCE_EMPTY_TEMPLATES)}
              </Typography>
            ) : showTemplateSelect ? (
              <Select
                control={control}
                name="templateID"
                label={t(TEMPLATES_PRODUCE_TEMPLATE)}
                variant="filled"
                fullWidth
                size="small"
                rules={optionalRules}
                options={templateOptions}
                validationChange={onValidateTemplateChange}
                actionsChange={onTemplateChange}
                disabled={isProducing}
                sx={selectSx}
              />
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Typography noWrap sx={{ fontWeight: 500 }}>
                  {templateName}
                </Typography>
                <Box
                  component="button"
                  type="button"
                  disabled={isProducing}
                  onClick={() => setIsPickingTemplate(true)}
                  sx={changeButtonSx}
                >
                  {t(TEMPLATES_PRODUCE_CHANGE)}
                </Box>
              </Box>
            )}
            {showFormatToggles ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography sx={{ fontWeight: 600 }}>
                  {t(TEMPLATES_PRODUCE_DIMENSIONS)}
                </Typography>
                <ProduceToggleChips
                  options={formatOptions}
                  selectedValues={selectedArtboardIDs}
                  onToggle={onToggleArtboard}
                  disabled={isProducing}
                  fullWidth
                />
              </Box>
            ) : null}
            <Select
              control={control}
              name="language"
              label={t(TEMPLATES_PRODUCE_LANGUAGE)}
              variant="filled"
              fullWidth
              size="small"
              rules={optionalRules}
              options={languageOptions}
              disabled={isProducing}
              sx={selectSx}
            />
            <Box>
              <Typography variant="body2" color="text.secondary">
                {t(TEMPLATES_PRODUCE_SUMMARY, {
                  rows: fields.length,
                  sizes: Math.max(selectedArtboardCount, 1),
                })}
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: "2.25rem", lineHeight: 1.2 }}>
                {totalCount}
              </Typography>
            </Box>
            <Button
              color="primary"
              fullWidth
              type="button"
              loading={isProducing}
              disabled={isProducing}
              onClick={onGenerate}
            >
              {t(TEMPLATES_PRODUCE_GENERATE, { count: totalCount })}
            </Button>
          </Box>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {!templateID ? (
              <Typography color="text.secondary">
                {t(TEMPLATES_PRODUCE_SELECT_TEMPLATE)}
              </Typography>
            ) : isLoadingDoc ? (
              <Loader hasMessage message={t(LOADING_INFORMATION)} />
            ) : columns.length === 0 ? (
              <Typography color="text.secondary">
                {t(TEMPLATES_PRODUCE_EMPTY_VARIABLES)}
              </Typography>
            ) : (
              <ProduceRowsTable
                key={templateID}
                control={control}
                columns={columns}
                fields={fields}
                rows={rows}
                invalidRowIndexes={invalidRowIndexes}
                pathStorage={pathStorage}
                disabled={!canEditRows}
                onAddRow={onAddRow}
                onRemoveRow={onRemoveRow}
                onChangeImage={onChangeImage}
                onUploadCsv={onUploadCsv}
                onDownloadFormat={onDownloadFormat}
                rowRefs={rowRefs}
              />
            )}
            {showGallery ? (
              <ProduceResultsGallery
                items={galleryItems}
                isGenerating={isProducing}
                pendingCount={pendingCount}
                onEditItem={onEditItem}
              />
            ) : null}
          </Box>
        </Box>
      )}
      <ModalCreativeTemplateEdit
        isOpen={Boolean(editItem)}
        onClose={() => setEditItem(null)}
        produceID={produceID}
        item={editItem}
      />
    </ContainerPage>
  );
};

export default TemplateProduce;
